import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './CheckedCombo.css';

/**
 * Shared CHECKED COMBO — a combobox whose list carries checkboxes, so several
 * values can be picked at once.
 *
 * Use it wherever one record legitimately covers more than one master-data
 * value (a DOC that qualifies an analyst on several test codes and methods, for
 * example). It is deliberately a *closed* list: callers pass the options, and
 * the user can only ever pick from them, so free-text variants of the same
 * value can never enter the data.
 *
 * Sibling: `features/modules/QueryCombo` is the SERVER-DRIVEN variant of this
 * control — same interaction, but it lazily loads a column's distinct values
 * and cascades against the other filters. This one takes a plain option list.
 * If a third variant is ever needed, refactor QueryCombo onto this base rather
 * than adding another.
 *
 * The popup is portaled to <body> so it is never clipped by a dialog's own
 * `overflow` — the record dialog scrolls its audit trail internally, and an
 * in-flow dropdown would be cut off at the card edge.
 */
export interface CheckedComboProps {
  /** The closed list of pickable values. */
  options: string[];
  /** Currently ticked values. */
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  /** Renders the required-field tint (BO UI Standard §4). */
  required?: boolean;
  disabled?: boolean;
  /** Shows "Loading…" in place of the list while options are being fetched. */
  loading?: boolean;
  ariaLabel?: string;
  /** Word used in the "N <unit> selected" summary, e.g. "tests". */
  unit?: string;
  /** OPTIONAL — when set, the trigger shows EVERY selected value joined by this
   *  separator (e.g. "; ") instead of collapsing to "N selected" past two picks.
   *  Truncates with an ellipsis when it overflows; the full list is on hover.
   *  Mirrors the legacy XAF editor that lists all names in the box. Omit to keep
   *  the compact "N selected" summary. */
  summaryJoin?: string;
  /** OPTIONAL — paginate the (filtered) list at this many rows per page and show
   *  a numbered pager (‹ 1 2 3 … N › + "Page X of N (M items)") at the bottom,
   *  mirroring the legacy XAF CheckedListBoxEditor. Omit (default) to keep the
   *  plain scrolling list — every existing usage is unchanged. Search still
   *  filters first; pagination applies to the filtered result. */
  pageSize?: number;
  /** OPTIONAL — hide the "OK" button in the tools row (the popup still closes on
   *  click-away / Escape). Matches the legacy checked-list which has no OK. */
  hideOk?: boolean;
}

export function CheckedCombo({
  options, values, onChange, placeholder = 'Select…',
  required, disabled, loading, ariaLabel, unit = 'selected', pageSize, summaryJoin, hideOk,
}: CheckedComboProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 220 });
  const btnRef = useRef<HTMLButtonElement>(null);

  const openPanel = () => {
    if (disabled) return;
    const r = btnRef.current?.getBoundingClientRect();
    if (r) {
      // Flip above the trigger when there isn't room below, so the list is
      // never half off-screen at the bottom of a tall dialog.
      const below = window.innerHeight - r.bottom;
      const height = Math.min(300, Math.max(180, below - 12));
      const openUp = below < 200 && r.top > below;
      setPos({
        top: openUp ? r.top + window.scrollY - height - 4 : r.bottom + window.scrollY + 2,
        left: r.left + window.scrollX,
        width: r.width,
      });
    }
    setQuery('');
    setPage(0);
    setOpen(o => !o);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // Filtering changes the result set, so jump back to the first page.
  useEffect(() => { setPage(0); }, [query]);

  const shown = query
    ? options.filter(o => o.toLowerCase().includes(query.toLowerCase()))
    : options;

  // Optional pagination (legacy CheckedListBoxEditor parity). Search filters
  // first; the pager walks the filtered result.
  const paging = !!pageSize && pageSize > 0;
  const pageCount = paging ? Math.max(1, Math.ceil(shown.length / pageSize!)) : 1;
  const curPage = Math.min(page, pageCount - 1);
  const listed = paging ? shown.slice(curPage * pageSize!, (curPage + 1) * pageSize!) : shown;
  const pageWindow = () => {
    const maxBtns = 7;
    let start = Math.max(0, curPage - Math.floor(maxBtns / 2));
    const end = Math.min(pageCount, start + maxBtns);
    start = Math.max(0, end - maxBtns);
    return Array.from({ length: end - start }, (_, i) => start + i);
  };

  const toggle = (v: string) => {
    const set = new Set(values);
    if (set.has(v)) set.delete(v); else set.add(v);
    // Kept in the OPTION list's order, not click order, so the same selection
    // always renders (and hashes) identically.
    onChange(options.filter(o => set.has(o)).concat(
      [...set].filter(v2 => !options.includes(v2))));
  };

  const allShownSelected = shown.length > 0 && shown.every(v => values.includes(v));

  const summary = values.length === 0 ? placeholder
    : summaryJoin != null ? values.join(summaryJoin)
    : values.length <= 2 ? values.join(', ')
    : `${values.length} ${unit}`;

  return (
    <>
      <button
        ref={btnRef} type="button" disabled={disabled}
        aria-label={ariaLabel} aria-haspopup="listbox" aria-expanded={open}
        className={`ckc-trigger${required ? ' req' : ''}${disabled ? ' disabled' : ''}`}
        onClick={openPanel}
        title={summaryJoin != null
          ? (values.length ? values.join(summaryJoin) : undefined)
          : (values.length > 2 ? values.join(', ') : undefined)}
      >
        <span className={values.length ? 'ckc-val' : 'ckc-ph'}>
          {loading ? 'Loading…' : summary}
        </span>
        <i className="ti ti-chevron-down ckc-chev" />
      </button>

      {open && createPortal(
        <>
          <div className="ckc-backdrop" onMouseDown={() => setOpen(false)} />
          <div className="ckc-pop" role="listbox" aria-multiselectable="true"
            style={{ top: pos.top, left: pos.left, minWidth: Math.max(220, pos.width) }}>
            <div className="ckc-search">
              <i className="ti ti-search" />
              <input autoFocus value={query} placeholder="Type to filter…"
                onChange={e => setQuery(e.target.value)} />
            </div>
            <div className="ckc-tools">
              <button type="button" className="ckc-tool" disabled={allShownSelected}
                onClick={() => onChange([...new Set([...values, ...shown])])}>
                Select all
              </button>
              <button type="button" className="ckc-tool red" disabled={values.length === 0}
                onClick={() => onChange([])}>Clear</button>
              <span style={{ flex: 1 }} />
              <span className="ckc-count">{values.length} selected</span>
              {!hideOk && (
                <button type="button" className="ckc-ok" onClick={() => setOpen(false)}>OK</button>
              )}
            </div>
            <div className="ckc-list">
              {loading && <div className="ckc-empty">Loading…</div>}
              {!loading && shown.length === 0 && <div className="ckc-empty">No matching values</div>}
              {!loading && listed.map(v => (
                <label key={v} className={`ckc-opt${values.includes(v) ? ' sel' : ''}`} title={v}>
                  <input type="checkbox" checked={values.includes(v)} onChange={() => toggle(v)} />
                  <span>{v}</span>
                </label>
              ))}
            </div>
            {!loading && paging && shown.length > pageSize! && (
              <div className="ckc-pager">
                <button type="button" className="ckc-pg" disabled={curPage === 0}
                  onClick={() => setPage(curPage - 1)} aria-label="Previous page">‹</button>
                {pageWindow().map(p => (
                  <button key={p} type="button"
                    className={`ckc-pg${p === curPage ? ' active' : ''}`}
                    onClick={() => setPage(p)}>{p + 1}</button>
                ))}
                <button type="button" className="ckc-pg" disabled={curPage >= pageCount - 1}
                  onClick={() => setPage(curPage + 1)} aria-label="Next page">›</button>
                <span className="ckc-pageinfo">Page {curPage + 1} of {pageCount} ({shown.length} items)</span>
              </div>
            )}
          </div>
        </>,
        document.body,
      )}
    </>
  );
}
